import React, { useEffect, useState } from 'react';
import { Paper, Typography, CircularProgress, Box, Collapse, IconButton, Link } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import api from '../utils/api';

/**
 * AI 本集摘要面板。
 * 從 /programs/:id/summary 取 AI 產的 Markdown 摘要（本集總覽、重點時間軸），
 * 與 radiko 的節目簡介（program.summary）不同——這是這一集實際內容的整理。
 * 摘要裡的 [MM:SS] 時間戳可點擊，跳轉到播放器對應時間。
 */
function SummaryPanel({ programId, getPlayer }) {
  const [md, setMd] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false); // 預設摺疊，點標題展開

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(`/programs/${programId}/summary`, { responseType: 'text' })
      .then((res) => {
        if (cancelled) return;
        setMd(typeof res.data === 'string' ? res.data : '');
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // 404 = 此節目尚無 AI 摘要 → 靜默隱藏（選配功能）；其他錯誤才提示
        if (err?.response?.status !== 404) setError('無法載入摘要');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [programId]);

  const parseTimeToSeconds = (timeStr) => {
    const parts = timeStr.split(':').map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return 0;
  };

  const seekTo = (seconds) => {
    const player = getPlayer && getPlayer();
    if (player) {
      player.currentTime = seconds;
      if (player.paused) player.play();
    }
  };

  // 404 / 空摘要 → 不渲染（避免空白外框）
  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="body2" color="text.secondary">{error}</Typography>
      </Paper>
    );
  }
  if (!loading && !md) return null;

  // 把 [MM:SS] / [HH:MM:SS] 轉成 markdown 連結（url 用 seek:<秒> 供自訂 a 元件跳轉）。
  // AI 摘要常把時間戳寫成行內程式碼 `[00:00:17]`（帶反引號），要連同外層反引號
  // 一起換掉，否則會被 react-markdown 當程式碼原樣顯示、不解析成連結。
  const processedMd = md.replace(
    /`?\[((\d{1,2}:)?\d{1,2}:\d{2})\]`?/g,
    (_m, timeStr) => `[${timeStr}](seek:${parseTimeToSeconds(timeStr)})`
  );

  const mdComponents = {
    a: ({ href, children }) => {
      if (href && href.startsWith('seek:')) {
        const seconds = Number(href.slice(5));
        return (
          <Link
            component="button"
            type="button"
            onClick={() => seekTo(seconds)}
            sx={{ verticalAlign: 'baseline', fontWeight: 600 }}
          >
            [{children}]
          </Link>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    },
  };

  return (
    <Paper variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
      {/* 可點的標題列：點整列展開/收合 */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 2, py: 1, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: open ? 1 : 0, borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="subtitle2">本集摘要（AI）</Typography>
        {loading ? (
          <CircularProgress size={18} />
        ) : (
          <IconButton size="small" sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            px: 2, py: 1,
            // 讓 markdown 的標題/清單間距舒服，字級貼近 MUI body
            '& h2': { fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 0.5 },
            '& h3': { fontSize: '0.95rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
            '& p': { my: 0.5, fontSize: '0.9rem', lineHeight: 1.7 },
            '& ul': { my: 0.5, pl: 2.5 },
            '& li': { fontSize: '0.9rem', lineHeight: 1.7 },
            '& code': { bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: '0.85rem' },
          }}
        >
          <ReactMarkdown
            components={mdComponents}
            // 預設 urlTransform 會把非標準協定（seek:）清成空字串，這裡放行 seek:，其餘走預設安全過濾
            urlTransform={(url) => (url.startsWith('seek:') ? url : defaultUrlTransform(url))}
          >
            {processedMd}
          </ReactMarkdown>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default SummaryPanel;
